<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import IconButton from "../IconButton.vue";
import type { Slot } from "../../api/scheduling";
import { groupByDay, todayIn } from "../../composables/agendaTime";
import {
  dayMonth,
  longWeekday,
  relativeDay,
  shortWeekday,
  timeText,
} from "../../composables/agenda";
import { t } from "../../i18n";

/**
 * Free times to pick (decision 0022): a column per day (Hoje, Amanhã, the weekday) with its time
 * chips, a few days at a time. Arrow keys move between chips as in a grid; the chosen one is green.
 * `paged` shows the arrows to the next days; asking past the loaded ones emits `more`.
 */
const model = defineModel<string | null>({ default: null });
const props = withDefaults(
  defineProps<{
    slots: Slot[];
    timeZone: string;
    /** Days per page. */
    perPage?: number;
    paged?: boolean;
    /** More free times exist after the loaded ones. */
    hasMore?: boolean;
    loading?: boolean;
    /** Chips per day before "+N". */
    perDay?: number;
    now?: Date | null;
  }>(),
  {
    perPage: 5,
    paged: false,
    hasMore: false,
    loading: false,
    perDay: 6,
    now: null,
  },
);
const emit = defineEmits<{ more: [] }>();

const page = ref(0);
const showAll = ref(false);
const grid = ref<HTMLElement>();
const today = computed(() => todayIn(props.timeZone, props.now ?? new Date()));
const days = computed(() =>
  groupByDay(props.slots, (s) => s.start, props.timeZone),
);
const pages = computed(() =>
  Math.max(1, Math.ceil(days.value.length / props.perPage)),
);
const shown = computed(() =>
  days.value.slice(
    page.value * props.perPage,
    (page.value + 1) * props.perPage,
  ),
);
const clipped = computed(() =>
  shown.value.some((d) => d.items.length > props.perDay),
);
watch(
  () => props.slots[0]?.start,
  () => {
    page.value = 0;
    showAll.value = false;
  },
);
// The chosen time stays in view when the list reloads.
watch(model, (start) => {
  if (!start) return;
  const i = days.value.findIndex((d) => d.items.some((s) => s.start === start));
  if (i >= 0) page.value = Math.floor(i / props.perPage);
});
/** "qui 23/10" under Hoje and Amanhã; the date alone under a weekday. */
function daySub(day: string): string {
  return relativeDay(day, today.value) === longWeekday(day)
    ? dayMonth(day)
    : `${shortWeekday(day).toLowerCase()} ${dayMonth(day)}`;
}
function next() {
  if (page.value + 1 < pages.value) page.value++;
  else if (props.hasMore) {
    emit("more");
    page.value++;
  }
}
const canNext = computed(() => page.value + 1 < pages.value || props.hasMore);

/** Only one chip is in the tab order: the chosen one, else the first. */
const focusStart = computed(() => {
  const all = shown.value.flatMap((d) => visible(d.items));
  return all.find((s) => s.start === model.value)?.start ?? all[0]?.start;
});
function visible(items: Slot[]): Slot[] {
  return showAll.value ? items : items.slice(0, props.perDay);
}
function key(e: KeyboardEvent, col: number, row: number) {
  const cols = shown.value.map((d) => visible(d.items));
  let c = col;
  let r = row;
  if (e.key === "ArrowDown") r++;
  else if (e.key === "ArrowUp") r--;
  else if (e.key === "ArrowRight") c++;
  else if (e.key === "ArrowLeft") c--;
  else if (e.key === "Home") r = 0;
  else if (e.key === "End") r = (cols[c]?.length ?? 1) - 1;
  else return;
  e.preventDefault();
  if (c < 0 || c >= cols.length) return;
  r = Math.max(0, Math.min(r, (cols[c]?.length ?? 1) - 1));
  const target = cols[c]?.[r];
  if (!target) return;
  void nextTick(() =>
    grid.value
      ?.querySelector<HTMLElement>(`[data-start="${target.start}"]`)
      ?.focus(),
  );
}
</script>
<template>
  <div class="slot-grid-wrap">
    <div v-if="paged" class="slot-grid-nav">
      <IconButton
        icon="chevronLeft"
        :label="t('book.days_before')"
        :disabled="page === 0"
        @click="page--"
      />
      <span class="small muted grow">{{
        shown.length
          ? t("book.days_range", {
              from: dayMonth(shown[0]!.day),
              to: dayMonth(shown[shown.length - 1]!.day),
            })
          : ""
      }}</span>
      <IconButton
        icon="chevron"
        :label="t('book.days_after')"
        :disabled="!canNext"
        @click="next"
      />
    </div>
    <div
      v-if="shown.length"
      ref="grid"
      class="slot-grid"
      role="group"
      :aria-label="t('book.slots')"
      :style="{ '--slot-cols': perPage }"
    >
      <div
        v-for="(d, col) in shown"
        :key="d.day"
        class="slot-col"
        role="group"
        :aria-label="`${relativeDay(d.day, today)} ${daySub(d.day)}`"
      >
        <div class="slot-day" aria-hidden="true">
          {{ relativeDay(d.day, today) }}
          <small>{{ daySub(d.day) }}</small>
        </div>
        <button
          v-for="(s, row) in visible(d.items)"
          :key="s.start"
          type="button"
          class="slot-chip"
          :class="{ on: model === s.start }"
          :aria-pressed="model === s.start"
          :tabindex="s.start === focusStart ? 0 : -1"
          :data-start="s.start"
          @click="model = s.start"
          @keydown="key($event, col, row)"
        >
          {{ timeText(s.start, timeZone) }}
        </button>
        <span
          v-if="!showAll && d.items.length > perDay"
          class="slot-more small muted"
          >{{ t("book.more_times", { count: d.items.length - perDay }) }}</span
        >
      </div>
    </div>
    <p v-else-if="loading" class="muted small">{{ t("book.loading") }}</p>
    <p v-else class="muted small">{{ t("book.no_slots") }}</p>
    <button
      v-if="clipped"
      type="button"
      class="link-button small"
      @click="showAll = !showAll"
    >
      {{ showAll ? t("book.fewer_times") : t("book.all_times") }}
    </button>
  </div>
</template>
